import { useStoreState } from '@/store'
import Interlink from './elements/Interlink'

const BelowTableContent = () => {
  const { queryResponse } = useStoreState(state => state.search)
  const vowelRegex = '^[aieouAIEOU].*'
  const vowelMatched =
    queryResponse.location_data.primary_location.city.match(vowelRegex)
  const anchors = queryResponse.location_data.anchors

  const anchorLookup = (topic: string) =>
    anchors.find(anchor => anchor.topic === topic)?.target_url

  const { city, state } = queryResponse.location_data.primary_location

  return (
    <>
      <h2 className="mt-10 text-gray-900">
        What does {vowelMatched ? 'an' : 'a'} {city}, {state} agent do?
      </h2>
      <p className="text-gray-700">
        {city}{' '}
        <a href="https://www.bankrate.com/real-estate/what-is-a-real-estate-agent/">
          real estate agents
        </a>{' '}
        are licensed professionals who represent both buyers and sellers in real
        estate transactions. They are trained to help guide their clients
        through every aspect of the often-complex process of buying and selling
        homes. In exchange for their services, {city} agents are compensated
        with a percentage of the property’s sale price — typically this{' '}
        <a href="https://www.bankrate.com/real-estate/realtor-fees/">
          commission
        </a>{' '}
        comes to somewhere between 2 and 3 percent for each agent, and the
        amount is often negotiable. The full commission amount is typically paid
        by the seller.
      </p>
      <h2 className="text-gray-900">
        What&apos;s the difference between an agent, Realtor and broker?
      </h2>
      <p className="text-gray-700">
        You may hear different terms, such as{' '}
        <a href="https://www.bankrate.com/real-estate/real-estate-broker-vs-agent/">
          agent, Realtor and broker
        </a>
        , and wonder what the differences are. Here’s a breakdown:
      </p>
      <ul className="text-gray-700">
        <li>
          All <strong>real estate agents</strong> must take classes and pass an
          exam to earn their real estate license. Some specialize in particular
          facets of the industry, but all licensed agents are qualified to help
          a client buy or sell homes and property in the state(s) in which they
          are licensed. Agents who represent the buyer, or buyer’s agents, help
          the client find and purchase a home, whereas agents who represent the
          seller help prepare and market the home and find an appropriate buyer.
          Seller’s agents are often called listing agents, as they manage the
          listing of the home on the market.
        </li>
        <li>
          A real estate agent who is also a member of the{' '}
          <a href="https://www.bankrate.com/real-estate/what-is-national-association-of-realtors/">
            National Association of Realtors
          </a>
          , an industry trade group, is called a <strong>Realtor</strong>. (The
          term is capitalized because it&apos;s a trademark.) All Realtors are
          agents, but not all agents are Realtors.
        </li>
        <li>
          A <strong>broker</strong> owns and/or manages a real estate brokerage
          or agency. Many brokers are also agents, but not all. However, all
          brokers must also take classes and pass a rigorous exam to earn their
          brokerage license.
        </li>
      </ul>
      <h2 className="text-gray-900">
        Do I need an agent? Questions to ask before hiring
      </h2>
      <p className="text-gray-700">
        It is possible to both{' '}
        <a href="https://www.bankrate.com/real-estate/should-i-buy-house-without-realtor/">
          buy a house without an agent
        </a>{' '}
        and{' '}
        <a href="https://www.bankrate.com/real-estate/do-i-need-an-agent-to-sell-my-house/">
          sell a house without an agent
        </a>
        . However, retaining one is usually a wise decision.{' '}
        <Interlink url={anchorLookup('home-buying')}>Buying</Interlink> or{' '}
        <Interlink url={anchorLookup('home-selling')}>
          selling a home in {city}
        </Interlink>{' '}
        is one of the biggest and most important financial decisions most people
        ever make, and the market is complex and constantly changing. It makes
        sense to have a qualified pro on your team, especially with so much
        money at stake.
      </p>
      <h3 className="text-gray-900">Selling a house in {city}</h3>
      <p className="text-gray-700">
        For sellers, a real estate agent who knows the{' '}
        <Interlink url={anchorLookup('housing-market')}>
          {city} housing market
        </Interlink>{' '}
        well can be a crucial partner. Pricing your home correctly is a tricky
        business, and agents can put together a{' '}
        <a href="https://www.bankrate.com/real-estate/comparative-market-analysis/">
          comparative market analysis
        </a>{' '}
        to make sure you understand what your home is worth — and how much area
        buyers will be willing to pay for it.
      </p>
      <h3 className="text-gray-900">Buying a house in {city}</h3>
      <p className="text-gray-700">
        On the buyer’s side, real estate agents know their market and what’s
        available in it. They can find you more options that meet your needs,
        and your budget, than you’d be able to find on your own, and they can
        open your eyes to other options or neighborhoods that you might not know
        about otherwise. When you find a place you love, a {city} buyer’s agent
        can also walk you through the offer and negotiation process to make sure
        you get the best deal possible.
      </p>
      <h2 className="text-gray-900">
        Finding an agent in {city}: What to look for
      </h2>
      <ul className="text-gray-700">
        <li>
          <strong>Local experience:</strong> Whatever market you’re located in,
          you want an agent who knows that market — well. This might mean
          everything from surrounding towns to specific neighborhoods, or even a
          single block. An agent with recent experience in the exact area you’re
          looking in will have valuable insight that less-localized agents
          won’t.
        </li>
        <li>
          <strong>Know-how:</strong> Ideally, you want someone who has done many
          deals like yours — if you’re selling a home, look for someone who’s
          sold similar homes nearby; if you’re buying a condo, see if you can
          find someone who has handled sales in the same building or complex.
          The more specifically an agent’s knowledge matches your needs, the
          more likely they are to get you the deal you want.
        </li>
        <li>
          <strong>Communication style:</strong> If you’re a texter, you won’t
          work well with someone who communicates exclusively via email.
          Similarly, if you like to talk things over on the phone, you won’t
          click with someone who only texts. Make sure you’re on the same page
          with your agent’s style, including whether to expect replies on
          evenings/weekends, how often to expect updates and who your main point
          of contact will be (the agent themselves, or someone on their team?).{' '}
        </li>
      </ul>
      <h2 className="text-gray-900">FAQs</h2>
      <h3 className="text-gray-900">
        Do I need a real estate agent in {city}, {state}?
      </h3>
      <p className="text-gray-700">
        Every real estate market is complicated in its own unique ways, and no
        one knows the local market better than an agent who operates in that
        area. You could buy or sell a home in {city}, {state} without one, but
        local agents have knowledge that can prove invaluable — both financially
        and in terms of guiding the way to a smooth and successful transaction.
      </p>
      <h3 className="text-gray-900">
        When should I first contact a real estate agent in {city}, {state}?
      </h3>
      <p className="text-gray-700">
        You can reach out to a real estate agent informally anytime, but before
        you sign a deal with one, make sure you’re really ready to buy or sell.
        Most agent contracts have time limits built in, and you don’t want to
        waste their time, or yours. If you’re buying, it’s smart to get
        preapproved for a mortgage before you start house-hunting, so that you
        have a solid idea of your budget and don’t bother with listings that
        aren’t realistically within your means. For sellers, you might consult
        with an agent on the earlier side, to get an expert opinion on whether
        your home in {city}, {state} is ready to show or needs some repairs or
        curb appeal help first.
      </p>
      <h3 className="text-gray-900">
        What type of real estate agent should I hire in {city}, {state}?
      </h3>
      <p className="text-gray-700">
        All licensed agents in {city}, {state} should be qualified to help you,
        but some specialize in certain areas. If you’re selling a home, look for
        a listing agent with plenty of successful sales under their belt —
        preferably in your specific neighborhood. If you’re looking to buy, the
        same guideline applies: Try to find a real estate agent who has helped
        others buy homes similar to what you want, in the areas you’re
        interested in. Some {city}, {state} Realtors may have special
        certifications to help specific kinds of clients, such as seniors or
        members of the military, and some specialize in specific kinds of
        properties as well, like vacation homes or condos.
      </p>
    </>
  )
}

export default BelowTableContent
